/*
    AGM Planning

    This component allows you to import AGMs from an excel file
    You can select an agm by clicking on the map - this part comes second

    should do basic fform validation on points added before being sent to the server or db

    TODO:
    - download agm planning template
    - add poi/subchain modal

    The table on this page needs these types of fields (based on AGM planning in original site):
        - column for map icon that brings up map modal with lat/long fields, submit, and cancel
    
*/
import React, {useState, useEffect, useCallback} from 'react';
//components
import { Button } from '@components/ui/buttons/Button';
import EditableTable from '@components/ui/editableTable/EditableTable';
import { ValidationErrors } from '@components/ui/messages/ValidationErrors';
import { RealChainValidationErrors } from '@components/ui/messages/RealChainValidationErrors';
import { AddRowDialog } from '../shared/AddRowDialog';
//table config
import { AGMPlanningSchema } from '../tableConfig/jobControlSchemas';
import { AGMPlanningColumnNameMap } from "../tableConfig/jobControlColumnNameMaps";
//hooks
import useConfirmDialog from '@hooks/useConfirmDialog';
import { useImportExcel } from '@hooks/useImportExcel';
import { useLoading } from '@contexts/LoadingContext';
import { useError } from '@contexts/ErrorContext';
import { useUnsavedChanges } from '@contexts/UnsavedChangesContext';
import { useSuccess } from '@contexts/SuccessContext';
//api and helper functions
import { getAGMsAPI, createAGMsAPI, updateAGMsAPI, deleteAGMsAPI, getAgmTemplate} from "@services/agmsAPI";
import { areObjectsEqual } from "@utils/areObjectsEqual";
import { handleAddRow } from '../helperFunctions/handleAddRow';
import { resetStateAfterDelete } from "../helperFunctions/resetStateAfterDelete";
import { validateTableData } from '../helperFunctions/validateTableData';
import { validateRealChain, validateEstChain } from '../helperFunctions/validateRealChain'; //eslint-disable-line

const initialData = [
    {
        "Site_Name": "",
        "Real_Chain": "",
        "Est_Lat": "",
        "Est_Lng": "",
        "Planning_Notes": "",
        "Address": "",
        "Access_Notes": ""
    }
]

/**
 * 
 * @param {DropdownOption} selectedJob
 * 
 * @returns {JSX.Element}
 * 
 * @category Components - Job Control
 * 
 * @TODO if we start using this page again (someone requests we have this instead of just using AGM Edit), needs updating - table currently doesn't lock any columns when job locked
 */
export const AGMPlanning = ({selectedJob, setActiveComponent}) => {
    //set state
    const [data, setData] = useState(initialData);
    const [originalData, setOriginalData] = useState([]); //use to compare original and changed data when time to submit

    const [validationErrors, setValidationErrors] = useState([]);
    const [estChainValidationErrors, setEstChainValidationErrors] = useState([]);

    const [showAddRow, setShowAddRow] = useState(false)

    const {FileUploadButton, fileName, setFileName, ImportColumnsMissingList, setMissingColumns} = useImportExcel()
    const [ConfirmDialog, requestConfirm] = useConfirmDialog()
    const { showLoading, hideLoading } = useLoading()
    const {showError} = useError()
    const {setHasUnsavedChanges} = useUnsavedChanges()
    const { timeoutSuccess } = useSuccess()


    //fetch agms
    const fetchAGMs = async(jobId) => {
        showLoading("Getting AGMs...");

        let response = await getAGMsAPI(jobId);

        if(!response.success){
            hideLoading()

            showError(`Error getting AGMs: ${response.message}`)
            
        } else {

            if(response.data.length > 0){

                const agms = response.data.sort((a,b) => a.Est_Chain - b.Est_Chain) //put the AGMs in order

                //remove CreatedAt since that will never be updated in the frontend
                //formatting boolean fields to match dropdown options
                const formattedData = agms.map(({CreatedAt, ...agm}) => {
                    return {
                        ...agm,
                    }
                })

                setData(formattedData);
                setOriginalData(structuredClone(formattedData))
                hideLoading()
                return
            }

            //if no data was returned from the fetch, reset state to initial data
            setData(initialData);
            hideLoading()
        }
    }

    //fetch agms when component loads or if job selected/changed
    useEffect(() => {
        if(!selectedJob) return 

        fetchAGMs(selectedJob.value)
        setValidationErrors([])

    }, [selectedJob]); //eslint-disable-line

    //helper functions
    const handleSubmit = async() =>{
        try {
            //reset validation error state
            setValidationErrors([])
            setEstChainValidationErrors([])

            //show loading message, remove old submit message if exists
            showLoading("Submitting...")

            //validation for real chain values
            let validationErrors = []
            let estChainErrors = []

            //check if required fields in table are filled in
            const isValid = validateTableData(data, setValidationErrors, AGMPlanningSchema);

            if(!isValid){
                console.log('not valid')
                hideLoading()
                return
            } 



            /**
             * Changes made by Bryan - This is now validating the Estimated Chain (Est_Chain)
             * Might need to throw this in a try-catch - if error happens in validateEstChain - Nothing happens (Submit button breaks)
             */
            //check each pair of agms
            for(let i = 0; i < data.length - 1; i++){
                
                const estChainInValid = validateEstChain(data[i], data[i + 1]) //check to make sure realchain >= haversine between agm points
                
                //if not valid, highlight both boxes in the table, and add to list of real chain errors
                if(estChainInValid){
                    validationErrors.push({ row: i, column: "Est_Chain" })
                    validationErrors.push({ row: i + 1, column: "Est_Chain" })

                    estChainErrors.push(estChainInValid)
                }
            }
            // setRealChainValidationErrors(realChainErrors);

            //if any real chain errors, ask user if they want to submit anyway
            if(estChainErrors.length > 0){
                setValidationErrors(validationErrors);
                setEstChainValidationErrors(estChainErrors);
                console.log('here')
                const isConfirmed = await requestConfirm('Some distances between sites may be shorter than expected (see notes below AGM table and highlighted boxes). Would you like to submit anyway?', 'Yes, submit', 'Cancel');

                if(!isConfirmed){
                    hideLoading();
                    return
                }
            }

            

            //submit agms to api  
            //api expects arrays of agms, so create empty arrays to push new/updated agms into
            let newAGMs = []
            let updatedAGMs = []

            for(const agm of data){

                //UPDATE AGMs - AGMs that are not new (have a AGMID) and are different from the original data (to reduce api calls)
                if(agm.hasOwnProperty("AGMID")){

                    //compare to original data to see if AGM data has changed
                    const originalRow = originalData.find(original => original.AGMID === agm.AGMID)

                    if(!areObjectsEqual(agm, originalRow)){
                        updatedAGMs.push(agm);
                    }
                } else {
                    //CREATE AGMs - if no AGMID, it's a new AGM
                    const newAGM = {
                        ...agm,
                        JobID: selectedJob.value,
                    }
                    newAGMs.push(newAGM);
                }
            }

            //if no changes made, no need to update state, also don't want to tell user it saved when nothing happened
            if(newAGMs.length === 0 && updatedAGMs.length === 0){

                setHasUnsavedChanges(false);

                hideLoading();
                
                timeoutSuccess('No changes');

                return;
            }

            //submit updated AGMs are if there are any
            if(updatedAGMs.length > 0){
                const response = await updateAGMsAPI(updatedAGMs);
                        
                if(!response.success){

                    showError(`Error updating AGMs: ${response.message}`);

                    hideLoading();

                    return;
                }
            }

            //get all the agms that aren't new, for updating state later
            let dataToUpdate = data.filter(agm => agm.hasOwnProperty("AGMID"))

            //submit new AGMS are if there are any
            if(newAGMs.length > 0){
                const response = await createAGMsAPI(newAGMs);
                            
                if(!response.success){
                    showError(`Error creating AGMs: ${response.message}`);

                    hideLoading();
                
                    return;
                }
                
                //format new agms, now with AGMIDs
                const AGMsWithIDs = response.data.map(({CreatedAt, ...agm}) => {
                    return {
                        ...agm,
                        Verified: agm.Verified === true ? "Yes" : "No",
                        GPS_Taken: agm.GPS_Taken === true ? "Yes" : "No"
                    }
                });

                dataToUpdate.push(...AGMsWithIDs);
            }
            //update state
            setData(dataToUpdate);
            setOriginalData(dataToUpdate);

            //if excel import had missing columns, reset message
            setMissingColumns([]);


        //if excel import had missing columns, reset message
        setMissingColumns([])

        //remove excel file name from page
        setFileName(null)
        
        setHasUnsavedChanges(false)
        
        hideLoading()
        
        timeoutSuccess('Saved')


            timeoutSuccess('Saved');

            //when agms are submitted directs user to AGM Edit component
            //This will ensure Real Chain gets completed with correct data
            setActiveComponent('AGMEdit'); //added by Bryan
        } catch {
            //if during real chain validation, user chooses not to submit, just hide loading modal
            hideLoading();
        }
    }

    //delete agms 
    
    const handleDeleteOneAGM = useCallback(async(rowData, rowIndex) => {
        showLoading("Deleting AGM...");

        const AGMID = rowData.AGMID;

        //if there is no ID, doesn't exist yet in database, so don't need to call API
        //just delete from table in that case
        if(AGMID){
            const response = await deleteAGMsAPI([AGMID]);

            if(!response.success){

                showError(`Error deleting AGM: ${response.message}`);

                hideLoading();
                return;
            }
        }
        //delete successful - remove item from data and let user know
        resetStateAfterDelete(setData, rowIndex, initialData);
        
        hideLoading();
        
        timeoutSuccess('Delete successful');

    }, [ showLoading, deleteAGMsAPI, hideLoading, resetStateAfterDelete,initialData, setData]); //eslint-disable-line


    const handleDeleteAllAGMs = async() => {
        //check that user wants to delete agms
        const isConfirmed = await requestConfirm('Are you sure you want to delete all AGMs?', 'Delete AGMs', 'Cancel');
        if (!isConfirmed) return;
            
        // user confirmed
        showLoading("Deleting AGMs...");

        //if agm has id, it exists in database, so need to delete it from there
        const deleteAGMsFromDB = data
            .filter(agm => agm.AGMID !== undefined)
            .map(agm => agm.AGMID);

        if(deleteAGMsFromDB.length > 0){

            const response = await deleteAGMsAPI(deleteAGMsFromDB);

            if(!response.success){
                    
                showError(`Error deleting AGMs: ${response.message}`)

                hideLoading();
                return;
            }
        }   
        //everything submitted successfully, so let user know and reset table
    

            setData(initialData);

            //if excel import had missing columns, reset message
            setMissingColumns([])

            //remove excel file name from page
            setFileName(null)

    
        setHasUnsavedChanges(false);

        hideLoading();
            
        timeoutSuccess('Delete successful');
    }

    //no POIs yet, come back to this later

    // const handleAddPOISC = () => {
    //     alert("add POI/subchain modal opens")

    // }

    
    const handleDownloadTemplate = async () => {
        //download logic
        showLoading('Getting Template');
        let response = await getAgmTemplate();
        if (response && response.success) {
            hideLoading();
            const link = document.createElement('a');
            link.href = response.url;
            link.setAttribute('download', ''); // Optionally specify a filename
            document.body.appendChild(link);
            link.click();
            link.remove();
        } else {
            hideLoading();
            showError(response.message);
        }     
    }


    return (
        <div className='martrak-max-width'>

            {selectedJob ? 
            (
                <>
                    {/* excel related buttons*/}
                    <div className='flex justify-end'>

                        <Button 
                            variant='noBackground' 
                            onClick={handleDownloadTemplate}
                            className="text-sm"
                        >
                            <i className="fa-solid fa-circle-arrow-down text-purple-800 mr-2"></i>
                            Download Template
                        </Button>

                        <FileUploadButton 
                            setData={setData}
                            data={data}
                            route="agms"
                            columnMap={AGMPlanningColumnNameMap}
                            schema={AGMPlanningSchema}
                        /> 

                    </div>

                    {/* import error messages */}
                    <ImportColumnsMissingList />

                    {/* imported file name, if any */}
                    {fileName ? <p className='my-4 text-center italic'>{fileName}</p> : null}

                    {/* table */}
                    <EditableTable 
                        data={data} 
                        setData={setData} 
                        schema={AGMPlanningSchema} 
                        canDeleteRow={true} 
                        handleDeleteRow={handleDeleteOneAGM}
                        validationErrors={validationErrors}
                    />

                    {/* submit, add row/poi, delete buttons */}
                    <div className='flex justify-end mb-5'>
                        <Button variant="success"onClick={() => setShowAddRow(true)} className='w-max mr-2'><i className="fa-solid fa-circle-plus mr-2"></i>Add Row</Button>

                        {/* no POIs yet, come back to this later */}
                        {/* <Button variant="success"onClick={handleAddPOISC} className='w-max'><i className="fa-solid fa-circle-plus mr-2"></i>Add POI/Subchain</Button> */}
                    </div>

                    <div className='flex'>
                        <Button variant="success"onClick={handleSubmit} className='w-max mr-2'>Submit</Button>
                        <Button variant="danger" onClick={handleDeleteAllAGMs} className='w-max'><i className="fa-solid fa-trash-can mr-2"></i>Delete All AGMs</Button>
                    </div>


                    {showAddRow &&
                        <AddRowDialog
                            dropdownOptions={data.map((AGM, i) => {return {value: AGM.AGMID, label: AGM.Site_Name, index: i}})}
                            showDialog={setShowAddRow}
                            onSubmit={(insertIndex, newData) => handleAddRow(AGMPlanningSchema, setData, insertIndex, newData)}
                            requiredFields={['Site_Name', 'Est_Chain', 'Est_Lat', 'Est_Lng']}
                            columnNameMap={AGMPlanningColumnNameMap}
                        />
                    }
                    

                    {/* error messages */}

                    {validationErrors.length > 0 && estChainValidationErrors.length === 0 
                        ? <ValidationErrors validationErrors={validationErrors} columnNameMap={AGMPlanningColumnNameMap}/> 
                        : null
                    }

                    {estChainValidationErrors.length > 0 &&
                        
                            <RealChainValidationErrors 
                                validationErrors={estChainValidationErrors} 
                            />

                        
                    }

                </>
            ) 
            : 

            (<p>Please select a job</p>)
            
            }

            {/* dialog box opens when "Delete All AGMs" button is clicked */}
            <ConfirmDialog />

        </div>   
    )
}